import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchPhqAssessment } from "../../../store/slices/phqAssessmentsSlice";
import logo from "../../../assets/images/logo-samplee.png";

export default function PHQFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { currentPhqAssessment, isLoading, error } = useSelector((state) => state.phqAssessments || {});

    useEffect(() => {
        if (id) dispatch(fetchPhqAssessment(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentPhqAssessment) {
            const timer = setTimeout(() => window.print(), 1000);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentPhqAssessment, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading PHQ-9 Report...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentPhqAssessment) return <div className="p-8 text-center">No record found.</div>;

    const d = currentPhqAssessment;

    // Mapping based on your exact backend response structure
    const phqItems = [
        { title: "Little interest or pleasure in doing things", scoreKey: "q1_little_interest_score", valueKey: "q1_little_interest_value" },
        { title: "Feeling down, depressed, or hopeless", scoreKey: "q2_feeling_down_score", valueKey: "q2_feeling_down_value" },
        { title: "Trouble falling/staying asleep, or sleeping too much", scoreKey: "q3_sleep_issues_score", valueKey: "q3_sleep_issues_value" },
        { title: "Feeling tired or having little energy", scoreKey: "q4_low_energy_score", valueKey: "q4_low_energy_value" },
        { title: "Poor appetite or overeating", scoreKey: "q5_appetite_issues_score", valueKey: "q5_appetite_issues_value" },
        { title: "Feeling bad about yourself or failure", scoreKey: "q6_feeling_bad_score", valueKey: "q6_feeling_bad_value" },
        { title: "Trouble concentrating on things", scoreKey: "q7_concentration_score", valueKey: "q7_concentration_value" },
        { title: "Moving/Speaking slowly or being restless", scoreKey: "q8_movement_changes_score", valueKey: "q8_movement_changes_value" },
        { title: "Thoughts of being better off dead or hurting self", scoreKey: "q9_self_harm_score", valueKey: "q9_self_harm_value" }
    ];

    const totalScore = phqItems.reduce((sum, item) => sum + (parseInt(d[item.scoreKey] || 0, 10)), 0);

    const getSeverity = (score) => {
        if (score <= 4) return "None-minimal depression";
        if (score <= 9) return "Mild depression";
        if (score <= 14) return "Moderate depression";
        if (score <= 19) return "Moderately severe depression";
        return "Severe depression";
    };

    const difficultyMap = {
        "not_difficult": "Not difficult at all",
        "somewhat": "Somewhat difficult",
        "very": "Very difficult",
        "extremely": "Extremely difficult"
    };

    return (
        <>
            <style jsx global>{`
                * { margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; font-family: 'Segoe UI', Arial, sans-serif !important; }
                body { background: #fff !important; color: #000 !important; font-size: 12px !important; }
                .print-container { padding: 30px !important; position: relative !important; }
                .logo { position: absolute !important; top: 15px !important; left: 15px !important; }
                .logo img { height: 60px !important; width: auto !important; }
                .header { text-align: center !important; margin-bottom: 20px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; }
                .header h3 { font-size: 1.5rem !important; font-weight: bold !important; }
                .meta-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 20px !important; }
                .meta-table td { border: 1px solid #000 !important; padding: 8px !important; }
                .label-bg { background: #f2f2f2 !important; font-weight: bold !important; width: 18% !important; }
                .phq-table { width: 100% !important; border-collapse: collapse !important; }
                .phq-table th, .phq-table td { border: 1px solid #000 !important; padding: 6px 10px !important; }
                .phq-table th { background: #eee !important; font-weight: bold !important; text-align: center !important; }
                .score-col { width: 60px !important; text-align: center !important; font-weight: bold !important; font-size: 13px !important; }
                .val-col { width: 150px !important; text-align: center !important; }
                .result-summary { margin-top: 20px !important; border: 2px solid #000 !important; display: flex !important; page-break-inside: avoid !important; }
                .result-score { background: #000 !important; color: #fff !important; padding: 10px 20px !important; text-align: center !important; min-width: 120px !important; }
                .result-score h2 { font-size: 1.8rem !important; margin: 0 !important; }
                .result-text { flex-grow: 1 !important; padding: 10px !important; text-align: center !important; display: flex !important; flex-direction: column !important; justify-content: center !important; }
                .severity-title { font-size: 1.2rem !important; font-weight: bold !important; text-transform: uppercase !important; }
                .difficulty-box { border: 1px solid #000 !important; padding: 10px !important; margin-top: 15px !important; background: #f9f9f9 !important; }
                .footer { margin-top: 25px !important; border-top: 1px solid #000 !important; padding-top: 8px !important; text-align: center !important; font-size: 9px !important; }
                .sig-row { display: flex !important; justify-content: space-between !important; margin-top: 40px !important; }
                .sig-box { border-top: 1px solid #000 !important; width: 220px !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; }
                @media print { .phq-table tr { page-break-inside: avoid !important; } }
            `}</style>

            <div className="print-container">
                <div className="logo"><img src={logo} alt="Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>PHQ-9 (PATIENT HEALTH QUESTIONNAIRE) REPORT</h6>
                </div>

                <table className="meta-table">
                    <tbody>
                        <tr>
                            <td className="label-bg">Client Name</td>
                            <td>{d.client_name}</td>
                            <td className="label-bg">Date</td>
                            <td>{new Date(d.created_at).toLocaleDateString('en-GB')}</td>
                        </tr>
                    </tbody>
                </table>

                <table className="phq-table">
                    <thead>
                        <tr>
                            <th style={{ width: '40px' }}>#</th>
                            <th>Over the last 2 weeks, how often have you been bothered by:</th>
                            <th className="val-col">Frequency</th>
                            <th className="score-col">Score</th>
                        </tr>
                    </thead>
                    <tbody>
                        {phqItems.map((item, index) => (
                            <tr key={index}>
                                <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                <td>{item.title}</td>
                                <td className="val-col">{d[item.valueKey]}</td>
                                <td className="score-col">{d[item.scoreKey]}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="result-summary">
                    <div className="result-score">
                        <small style={{ fontSize: '9px' }}>TOTAL SCORE</small>
                        <h2>{totalScore}</h2>
                    </div>
                    <div className="result-text">
                        <small style={{ fontSize: '9px' }}>DEPRESSION SEVERITY</small>
                        <div className="severity-title">{getSeverity(totalScore)}</div>
                    </div>
                </div>

                <div className="difficulty-box">
                    <strong>Level of Difficulty:</strong> {difficultyMap[d.difficulty] || d.difficulty}
                    <p style={{ fontSize: '10px', marginTop: '5px', fontStyle: 'italic' }}>
                        (Impact of these problems on work, home, or social life)
                    </p>
                </div>

                <div className="sig-row">
                    <div className="sig-box">Patient Signature</div>
                    <div className="sig-box">Assessing Practitioner Signature</div>
                </div>

                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
        </>
    );
}